import { useEffect, useRef, useState } from 'react';
import { Loader, Table, Group } from '@mantine/core';
import Header from '../../components/util/header';
import { useSelector } from 'react-redux';
import {
  fetchCashCollectionList,
  fetchCashCollectionReport,
  fetchCashCollectionXLS,
  fetchLog,
  Transaction,
  TransactionResponse
} from '../../../libraries/transactions';
import { AppState } from '../../client-redux/reducers';
import { LoginState } from '../../client-redux/login/reducer';
import { Language } from '../../languages/languageHandler';
import { formatCurrAmount } from '../../utils/script';
import PaginationComponent from '../../components/pagination';
import { ICurrency } from '../../../libraries/currencies';
import { ILocation } from '../../../libraries/location';
import { useFitTableRows } from '../../components/util/fit-table-rows-hook';
import FilterBar, { FilterBarFieldsData } from '../../components/filterbar';
import { formatDateTimeNSecLabel, formatDateToISO } from '../../utils/date';
import CustomModal from '../../components/custom-modal';
import ShowTransaction from './show-transactions';
import './styles.scss';

const hideColumns = {
  method: false,
  amountReceived: false,
  amountDispensed: false,
  doc: false,
  card: true,
  account: true,
  depositor: true,
  reason: true,
  declaration: true
};

const CashCollections = () => {
  const lang = useSelector<AppState, Language>(state => state.lang.lang);
  const login = useSelector<AppState, LoginState>(state => state.login);
  const locations = useSelector<AppState, ILocation[]>(state => state.locations.locations);
  const currencies = useSelector<AppState, ICurrency[]>(state => state.currencies.currencies);
  const controller = new AbortController();
  const [openDetails, setOpenDetails] = useState<boolean>(true);
  const [, setActiveRow] = useState<number>();
  const [selectedTransaction, setSelectedTransaction] = useState<Transaction>();
  const [selectedTransactionCurrency, setSelectedTransactionCurrency] = useState<ICurrency>();
  const [loading, setLoading] = useState(false);
  const [totalRowsResponse, setTotalRowsResponse] = useState<number | undefined>();
  const [offset, setOffset] = useState<number>(0);
  const {
    limit = 10,
    setLimit,
    tableRef
  } = useFitTableRows({
    bufferRows: 0,
    minHeightForDynamic: 400
  });
  const [currentPage, setCurrentPage] = useState<number>(0);
  const [list, setList] = useState<TransactionResponse>();
  const [noResults, setNoResults] = useState<boolean>(true);
  const [statusOperCode, setStatusOperCode] = useState<number>();
  const tableWrapperRef = useRef<HTMLDivElement>(null);
  const [filterBarData, setFilterBarData] = useState<FilterBarFieldsData>();

  const isDepositMachine = login.equipmentTypes ? login.equipmentTypes.some(e => Math.floor(e / 100) === 4) : false;
  const isPaymentMachine = login.equipmentTypes ? login.equipmentTypes.some(e => Math.floor(e / 100) !== 4) : false;

  const [showErrorDialog, setShowErrorDialog] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>('');

  useEffect(() => {
    if (isDepositMachine && !isPaymentMachine) {
      hideColumns.method = true;
      hideColumns.amountReceived = true;
      hideColumns.amountDispensed = true;
      hideColumns.doc = true;
    } // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (filterBarData && limit) {
      loadCashCollectionList();
    } // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterBarData, limit, offset]);

  const handleFilterBarOnDataChange = (data: FilterBarFieldsData) => {
    setFilterBarData(data);
    changePage(1);
  };

  const loadCashCollectionList = () => {
    if (loading) {
      controller.abort();
    }
    if (!filterBarData || !limit) {
      return;
    }
    setLoading(true);
    setList(undefined);
    const shouldFetchTotalRows = offset === 0;

    fetchCashCollectionList(
      controller,
      filterBarData.entity || 0,
      filterBarData.lid || 0,
      filterBarData.eid || 0,
      offset,
      limit,
      shouldFetchTotalRows,
      filterBarData.noteSN,
      filterBarData.transactionId,
      formatDateToISO(filterBarData.beginDateTime),
      formatDateToISO(filterBarData.endDateTime),
      filterBarData.uid,
      undefined
    )
      .then(response => {
        if (response.statusOper.code === 0) {
          setList(response);
          if (shouldFetchTotalRows && typeof response.totalRows === 'number') {
            setTotalRowsResponse(response.totalRows);
          }
          setNoResults(false);
          setStatusOperCode(response.statusOper.code);
        } else {
          setNoResults(true);
          setStatusOperCode(response.statusOper.code);
        }
      })
      .catch(error => {
        if (error.name === 'AbortError') {
        } else {
        }
      })
      .finally(() => setLoading(false));
  };

  // ----------------- Modal information with logs -------------------//
  const showTransactionDetails = (n: number) => {
    if (list) {
      setActiveRow(n);
      setSelectedTransaction(list?.transactions[n]);
      if (currencies) {
        let matchingCurrency = currencies.find(currency => currency.cid === list?.transactions[n].cid);
        setSelectedTransactionCurrency(matchingCurrency);
      }
      setOpenDetails(true);
    }
  };

  const getLog = () => {
    if (!selectedTransaction) {
      setErrorMessage(lang.TRANSACTION_NO_LOG);
      setShowErrorDialog(true);
      setTimeout(() => setShowErrorDialog(false), 2000);
      return;
    }

    try {
      fetchLog(controller, selectedTransaction.eid, selectedTransaction.period, selectedTransaction.code);
    } catch (err) {
      console.error(err);
      setErrorMessage(lang.TRANSACTION_LOG_ERROR);
      setShowErrorDialog(true);
      setTimeout(() => setShowErrorDialog(false), 2000);
    }
  };

  const getReport = async () => {
    if (!selectedTransaction) {
      setErrorMessage(lang.TRANSACTION_NO_LOG);
      setShowErrorDialog(true);
      setTimeout(() => setShowErrorDialog(false), 2000);
      return;
    }

    try {
      const response = await fetchCashCollectionReport(
        controller,
        selectedTransaction.eid,
        selectedTransaction.period,
        selectedTransaction.code
      );

      if (response.statusOper.code !== 0) {
        const message = `${lang.TRANSACTION_REPORT_ERROR} - ${response.statusOper.code}: ${response.statusOper.message}`;

        setErrorMessage(message);
        setShowErrorDialog(true);
        setTimeout(() => setShowErrorDialog(false), 2000);
      }
    } catch (err) {
      console.error(err);
      setErrorMessage(lang.TRANSACTION_LOG_ERROR);
      setShowErrorDialog(true);
      setTimeout(() => setShowErrorDialog(false), 2000);
    }
  };

  //----------------------------------------------------------------//

  const changePage = (page: number) => {
    const newOffset = (page - 1) * limit;
    setCurrentPage(page);
    setOffset(newOffset);
  };

  const resultsSelect = (newValue: number) => {
    const newLimit = Number(newValue);
    setLimit(newLimit);
    setCurrentPage(1);
    setOffset(0);
  };

  const getXLSReport = async () => {
    if (!filterBarData) return;

    try {
      await fetchCashCollectionXLS(
        filterBarData.entity || 0,
        filterBarData.lid || 0,
        filterBarData.eid || 0,
        controller,
        formatDateToISO(filterBarData.beginDateTime),
        formatDateToISO(filterBarData.endDateTime),
        filterBarData.uid
      );
    } catch (err: any) {
      console.error('XLS error:', err);
      const status = err?.response?.statusText || err.message;
      const message = `${lang.TRANSACTIONS_XLS_ERROR} - ${status}`;
      setErrorMessage(message);
      setShowErrorDialog(true);
      setTimeout(() => setShowErrorDialog(false), 2000);
    }
  };

  return (
    <div className="transactions">
      {openDetails && selectedTransaction && selectedTransactionCurrency ? (
        <ShowTransaction
          transaction={selectedTransaction}
          openDetails={openDetails}
          hideBtn={() => setOpenDetails(false)}
          lang={lang}
          currency={selectedTransactionCurrency}
          getLog={async () => getLog()}
          getReport={async () => getReport()}
        />
      ) : null}

      <Header pageTitle={lang.CASH_COLLECTIONS} />

      <FilterBar
        onDataChange={handleFilterBarOnDataChange}
        onSearch={loadCashCollectionList}
        fieldsUsage={{
          entity: true,
          lid: true,
          eid: true,
          uid: true,
          period: false,
          beginDateTime: true,
          endDateTime: true,
          noteSN: true,
          transactionId: true,
          financialOnly: false
        }}
        xlsReport={!noResults ? getXLSReport : undefined}
      />

      <div ref={tableWrapperRef}>
        <Table.ScrollContainer minWidth={500} ref={tableRef}>
          <Table striped highlightOnHover verticalSpacing="sm">
            <Table.Thead>
              <Table.Tr>
                <Table.Th>{lang.EQUIPMENT_LOCAL}</Table.Th>
                <Table.Th>{lang.EQUIPMENT_ID}</Table.Th>
                <Table.Th>{lang.TRANSACTION_ID}</Table.Th>
                <Table.Th>{lang.GLOBAL_DATE_TIME}</Table.Th>
                <Table.Th>{lang.GLOBAL_TOTAL_INVOICED}</Table.Th>
                <Table.Th>{lang.GLOBAL_TOTAL_COLLECTED}</Table.Th>
                <Table.Th>{lang.CASH_COLLECTION_EXCESS}</Table.Th>
              </Table.Tr>
            </Table.Thead>

            <Table.Tbody>
              {loading ? (
                <Table.Tr>
                  <Table.Td colSpan={16}>
                    <Loader color="#016273" />
                  </Table.Td>
                </Table.Tr>
              ) : (
                <>
                  {noResults ? (
                    <Table.Tr>
                      <Table.Td colSpan={16} style={{ textAlign: 'center' }}>
                        {lang.GLOBAL_NO_RESULTS}
                      </Table.Td>
                    </Table.Tr>
                  ) : (
                    list?.transactions.map((transaction, index) => {
                      console.log('Transaction:', transaction);
                      const matchingCurrency = currencies?.find(c => c.cid === transaction?.cid);
                      const matchingLocation = locations.find(shop => shop.sid === transaction.sid);
                      return (
                        <Table.Tr
                          key={index}
                          style={{ cursor: 'pointer' }}
                          onClick={() => showTransactionDetails(index)}
                        >
                          <Table.Td>{matchingLocation ? matchingLocation.name : ''}</Table.Td>
                          <Table.Td>{transaction.eid}</Table.Td>
                          <Table.Td>{`${transaction.eid}-${transaction.period}-${transaction.code}`}</Table.Td>
                          <Table.Td>{formatDateTimeNSecLabel(transaction.dateTime)}</Table.Td>
                          <Table.Td>
                            {matchingCurrency
                              ? formatCurrAmount(transaction.typeData.invoiced || 0, matchingCurrency)
                              : '-'}
                          </Table.Td>
                          <Table.Td>
                            {matchingCurrency ? formatCurrAmount(transaction.value || 0, matchingCurrency) : '-'}
                          </Table.Td>
                          <Table.Td>
                            {matchingCurrency
                              ? formatCurrAmount(transaction.typeData.excess || 0, matchingCurrency)
                              : '-'}
                          </Table.Td>
                        </Table.Tr>
                      );
                    })
                  )}
                </>
              )}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
      </div>

      <Group>
        <PaginationComponent
          total={totalRowsResponse}
          statusOperCode={statusOperCode}
          itemsPerPage={limit}
          dynamicLimit={limit}
          currentPage={currentPage}
          onPageChange={changePage}
          onResultsChange={resultsSelect}
          offset={offset}
        />
      </Group>

      <CustomModal
        opened={showErrorDialog}
        onClose={() => setShowErrorDialog(false)}
        message={errorMessage}
        backgroundColor="#E3353F"
        textColor="white"
      />
    </div>
  );
};

export default CashCollections;
